import React, { useState, useEffect } from 'react';

function UserForm({ initialData, onSubmit, onCancel, isSubmitting = false, mode = 'create' }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    firstName: '',
    lastName: '',
    profilePicture: '',
    dateOfBirth: '',
    userRole: 'User',
    bio: ''
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localSubmitting, setLocalSubmitting] = useState(false);

  useEffect(() => {
    if (initialData && mode === 'edit') {
      setFormData({
        username: initialData.username || '',
        password: '',
        confirmPassword: '',
        email: initialData.email || '',
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        profilePicture: initialData.profilePicture || '',
        dateOfBirth: initialData.dateOfBirth ? initialData.dateOfBirth.slice(0, 10) : '',
        userRole: initialData.userRole || 'User',
        bio: initialData.bio || ''
      });
    }
  }, [initialData, mode]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (mode === 'create') {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else if (mode === 'edit' && formData.password) {
      if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }
    
    setErrors(newErrors);
    setServerError('');
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const submitData = { ...formData };
    if (mode === 'edit' && !submitData.password) {
      delete submitData.password;
      delete submitData.confirmPassword;
    }
    
    setLocalSubmitting(true);
    setServerError('');
    
    try {
      // Call the parent onSubmit which should handle the API call
      const result = await onSubmit(submitData);
      
      // If we get here, the API call was successful
      // The parent will handle closing the modal and showing success
    } catch (err) {
      // Error is caught from parent - display the error message
      console.error('Submission error:', err);
      setServerError(err.message || 'An error occurred. Please try again.');
      setLocalSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (serverError) {
      setServerError('');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setServerError('Image size should be less than 2MB');
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setServerError('Please upload an image file');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove the data:image/jpeg;base64, prefix
        const base64String = reader.result.split(',')[1];
        setFormData(prev => ({ ...prev, profilePicture: base64String }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="admin-crud-form">
      {/* Server Error Display */}
      {serverError && (
        <div className="admin-crud-toast" style={{ position: 'relative', marginBottom: '20px' }}>
          <div className="admin-crud-toast-content admin-crud-toast-danger">
            <div className="admin-crud-toast-header">
              <strong>Error</strong>
              <button type="button" onClick={() => setServerError('')}>✕</button>
            </div>
            <div className="admin-crud-toast-body">{serverError}</div>
          </div>
        </div>
      )}

      <div className="admin-crud-two-columns">
        {/* Left Column */}
        <div>
          <div className="admin-crud-form-group">
            <label className="admin-crud-label admin-crud-label-required">Username</label>
            <input
              type="text"
              name="username"
              className={`admin-crud-input ${errors.username ? 'admin-crud-input-error' : ''}`}
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter username"
              disabled={localSubmitting || isSubmitting}
            />
            {errors.username && <div className="admin-crud-error-message">{errors.username}</div>}
          </div>

          <div className="admin-crud-form-group">
            <label className="admin-crud-label admin-crud-label-required">Email</label>
            <input
              type="email"
              name="email"
              className={`admin-crud-input ${errors.email ? 'admin-crud-input-error' : ''}`}
              value={formData.email}
              onChange={handleChange}
              placeholder="user@example.com"
              disabled={localSubmitting || isSubmitting}
            />
            {errors.email && <div className="admin-crud-error-message">{errors.email}</div>}
          </div>

          <div className="admin-crud-form-group">
            <label className="admin-crud-label">
              {mode === 'create' ? 'Password' : 'New Password (leave blank to keep current)'}
              {mode === 'create' && <span className="admin-crud-label-required"></span>}
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className={`admin-crud-input ${errors.password ? 'admin-crud-input-error' : ''}`}
                value={formData.password}
                onChange={handleChange}
                placeholder={mode === 'create' ? 'Enter password' : 'Enter new password'}
                style={{ flex: 1 }}
                disabled={localSubmitting || isSubmitting}
              />
              <button
                type="button"
                className="admin-crud-btn admin-crud-btn-outline"
                onClick={() => setShowPassword(!showPassword)}
                style={{ padding: '8px 16px' }}
                disabled={localSubmitting || isSubmitting}
              >
                {showPassword ? '👁️ Hide' : '👁️ Show'}
              </button>
            </div>
            {errors.password && <div className="admin-crud-error-message">{errors.password}</div>}
          </div>

          <div className="admin-crud-form-group">
            <label className="admin-crud-label">Confirm Password</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                className={`admin-crud-input ${errors.confirmPassword ? 'admin-crud-input-error' : ''}`}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm password"
                style={{ flex: 1 }}
                disabled={localSubmitting || isSubmitting}
              />
              <button
                type="button"
                className="admin-crud-btn admin-crud-btn-outline"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{ padding: '8px 16px' }}
                disabled={localSubmitting || isSubmitting}
              >
                {showConfirmPassword ? '👁️ Hide' : '👁️ Show'}
              </button>
            </div>
            {errors.confirmPassword && <div className="admin-crud-error-message">{errors.confirmPassword}</div>}
          </div>
        </div>

        {/* Right Column */}
        <div>
          <div className="admin-crud-form-group">
            <label className="admin-crud-label">First Name</label>
            <input
              type="text"
              name="firstName"
              className="admin-crud-input"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
              disabled={localSubmitting || isSubmitting}
            />
          </div>

          <div className="admin-crud-form-group">
            <label className="admin-crud-label">Last Name</label>
            <input
              type="text"
              name="lastName"
              className="admin-crud-input"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
              disabled={localSubmitting || isSubmitting}
            />
          </div>

          <div className="admin-crud-form-group">
            <label className="admin-crud-label">Date of Birth</label>
            <input
              type="date"
              name="dateOfBirth"
              className="admin-crud-input"
              value={formData.dateOfBirth}
              onChange={handleChange}
              disabled={localSubmitting || isSubmitting}
            />
          </div>

          <div className="admin-crud-form-group">
            <label className="admin-crud-label admin-crud-label-required">User Role</label>
            <select
              name="userRole"
              className="admin-crud-select"
              value={formData.userRole}
              onChange={handleChange}
              disabled={localSubmitting || isSubmitting}
            >
              <option value="User">User</option>
              <option value="Admin">Admin</option>
              <option value="Editor">Editor</option>
              <option value="Moderator">Moderator</option>
            </select>
          </div>
        </div>
      </div>

      <div className="admin-crud-form-group">
        <label className="admin-crud-label">Bio</label>
        <textarea
          name="bio"
          className="admin-crud-textarea"
          rows="3"
          value={formData.bio}
          onChange={handleChange}
          placeholder="Tell us about yourself..."
          disabled={localSubmitting || isSubmitting}
        />
      </div>

      <div className="admin-crud-form-group">
        <label className="admin-crud-label">Profile Picture</label>
        <input
          type="file"
          accept="image/*"
          className="admin-crud-input"
          onChange={handleImageUpload}
          disabled={localSubmitting || isSubmitting}
        />
        {formData.profilePicture && (
          <div style={{ marginTop: '12px' }}>
            <img
              src={`data:image/jpeg;base64,${formData.profilePicture}`}
              alt="Preview"
              style={{ maxWidth: '150px', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }}
            />
            <button
              type="button"
              className="admin-crud-btn admin-crud-btn-danger admin-crud-btn-sm"
              style={{ marginTop: '8px' }}
              onClick={() => setFormData(prev => ({ ...prev, profilePicture: '' }))}
              disabled={localSubmitting || isSubmitting}
            >
              Remove Image
            </button>
          </div>
        )}
        <small className="admin-crud-empty-subtext">
          Upload a profile picture (optional, max 2MB, JPG/PNG only)
        </small>
      </div>

      <div className="admin-crud-modal-footer" style={{ padding: '20px 0 0 0' }}>
        <button
          type="button"
          className="admin-crud-btn admin-crud-btn-secondary"
          onClick={onCancel}
          disabled={localSubmitting || isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="admin-crud-btn admin-crud-btn-primary"
          disabled={localSubmitting || isSubmitting}
        >
          {localSubmitting || isSubmitting 
            ? (mode === 'edit' ? 'Saving...' : 'Creating...') 
            : (mode === 'edit' ? 'Update User' : 'Create User')}
        </button>
      </div>
    </form>
  );
}

export default UserForm;